import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteProviderItem,
  getProvidersList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateProvidersBunch,
} from "store/gameCenter/providers/slice";
import { gameCenterLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import ToggleBtn from "components/buttons/ToggleBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import GameCenterFilters from "../GameCenterFilters";
import ProviderCurrenciesCell from "./components/ProviderCurrenciesCell";
import ProviderGeoRestrictionsCell from "./components/ProviderGeoRestrictionsCell";

import editIcon from "assets/icons/edit-icon.svg";
import listIcon from "assets/icons/list-icon.svg";
import gridIcon from "assets/icons/grid-icon.svg";

import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import { GuideButton } from "modals/guides/GuideButton";
import { useGuideModal } from "modals/guides/useGuideModal";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import ImageWithFallback from "helpers/ImageWithFallback";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import eyeIcon from "assets/icons/eye.svg";
import eyeOffIcon from "assets/icons/eye-off.svg";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";
import styles from "./List.module.scss";

const ProvidersList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { openGuide } = useGuideModal();
  const activeProviderId = searchParams.get("entityId");

  const permissions = usePermissionHelper();

  const [order, setOrder] = useState<{
    title: string;
    slug: string;
    countOfGames: string;
  }>({
    title: "none",
    slug: "none",
    countOfGames: "none",
  });
  const [activeView, setView] = useState<string>("table");
  const [currentLimit, setLimit] = useState<number>(gameCenterLimits[2]);
  const [search, setSearch] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([]);

  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
    { label: t("popular"), value: "popular" },
    { label: t("unpopular"), value: "unpopular" },
  ];

  const providersList = useAppSelector((store) => store.providers.list);
  const isListFetched = useAppSelector(
    (store) => store.providers.isListFetched,
  );
  const totalItems = useAppSelector((store) => store.providers.totalItems);
  const totalPages = useAppSelector((store) => store.providers.totalPages);
  const activePage = useAppSelector((store) => store.providers.activePage);
  const processingItemId = useAppSelector(
    (store) => store.providers.processingItemId,
  );
  const selectedItems = useAppSelector(
    (store) => store.providers.selectedItems,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'providers',
    isListFetched
  });

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getProvidersList({
          limit: currentLimit,
          offset: activePage - 1,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          title: search,
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };
  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.provider}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.provider }));
  };

  const handleDeleteItem = async (id: number) => {
    const resultTag = await dispatch(deleteProviderItem(id ?? 0));
    if (resultTag?.payload?.status === "OK") {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

const handleActionClick = async (e: string) => {
  if (!permissions?.update) {
    return;
  }

  if (e === "activate" || e === "hide") {
    await dispatch(
      updateProvidersBunch({
        ids: selectedItems,
        isActive: e === "activate" ? 1 : 0,
      }),
    );
  }

  if (e === "popular" || e === "unpopular") {
    await dispatch(
      updateProvidersBunch({
        ids: selectedItems,
        isPopular: e === "popular" ? 1 : 0,
      }),
    );
  }
};

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <GameCenterFilters
        searchValue={search}
        handleSearch={handleFilterSearch}
        selectedActive={active}
        handleUpdateActive={(e) => setActive(e)}
        handleApply={handleApply}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box page-list-header--box-actions">
            <ToggleBtn
              handleClick={(v) => setView(v)}
              activeValue={activeView}
              disabled={providersList.length === 0}
              icons={[
                {
                  url: listIcon,
                  value: "table",
                },
                {
                  url: gridIcon,
                  value: "grid",
                },
              ]}
            />
            <GuideButton onClick={() => openGuide("gamecentre")} />
            {permissions?.update && (
              <DefaultDropdown
                onSelect={(e) => {
                  handleActionClick(e as string);
                }}
                placeholder={t("chooseAction")}
                options={options}
                isSmall
                disabled={selectedItems.length === 0}
              />
            )}
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={8} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div
              className={`game-table-wrap ${activeView === "table" ? "" : "grid"}`}
            >
              {providersList.length === 0 && (
                <EmptyState message={t("noProvidersFound")} />
              )}

              {providersList.length > 0 && (
                <>
                  {activeView === "table" ? (
                    <Table
                      data={providersList}
                      order={order}
                      idField="id"
                      selectedItems={selectedItems}
                      onAllSelect={handleAllClick}
                      onSelect={(id) => handleItemClick(id)}
                      onEdit={(id) => handleOpenEditModal(id)}
                      hasEdit={permissions?.update}
                      hasView={!permissions?.update}
                      onView={(id) => handleOpenEditModal(id)}
                      activeRowId={activeProviderId}
                      processingId={processingItemId}
                      hasCheckbox={permissions?.update}
                      onSort={(id) => (id ? handleOrderClick(id) : {})}
                      hasDelete={false}
                      className={styles.providersTable}
                      columns={{
                        fixedColumns: [
                          "id",
                          "image",
                          "title",
                          "slug",
                          "countOfGames",
                          "isActive",
                          "geoRestrictions",
                          "currencies",
                        ],
                        image: {
                          imageFit: "contain",
                        },
                        hiddenColumns: ['icon']
                      }}
                      customColumns={(row) => {
                        return {
                          geoRestrictions: (
                            <ProviderGeoRestrictionsCell codes={row.geoRestrictions} />
                          ),
                          currencies: (
                            <ProviderCurrenciesCell currencies={row.currencies} />
                          ),
                        };
                      }}
                    />
                  ) : (
                    <div className="game-grid">
                      {providersList.map((el) => (
                        <div className="game-item big" key={el.id}>
                          <div className="game-item-img">
                            <ImageWithFallback
                              src={el.image as string}
                              alt={el.title}
                              className="game-item-img__image"
                            />
                          </div>
                          <div className="game-item-content">
                            <div className="game-item-content--title">
                              {el.title}
                            </div>
                            <div className="game-item-content--actions">
                              {permissions?.update && (
                                <div
                                  onClick={() => handleItemClick(el.id)}
                                  className={`def-check-control ${
                                    selectedItems.includes(el.id)
                                      ? "checked"
                                      : ""
                                  }`}
                                />
                              )}
                              <div className="provider">{el.countOfGames}</div>
                              <div
                                className="option"
                                onClick={() => handleOpenEditModal(el.id)}
                              >
                                <img
                                  src={
                                    permissions?.update
                                      ? editIcon
                                      : String(activeProviderId) === String(el.id)
                                        ? eyeOffIcon
                                        : eyeIcon
                                  }
                                  alt={
                                    permissions?.update
                                      ? "Edit Icon"
                                      : String(activeProviderId) === String(el.id)
                                        ? "Close View Icon"
                                        : "View Icon"
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {providersList.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={gameCenterLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProvidersList;
